import { Link } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { Background3D } from '../components/Background3D';
import { ArrowUpRight, Atom, Code, Zap } from 'lucide-react';

export function Models() {
  const { theme } = useTheme();
  
  const modelFamilies = [
    {
      name: "FRACTAL",
      description: "Constraint-based protein structure prediction system leveraging ESM-2 3B for geometric inference. Two-stage architecture: neural constraint prediction followed by physics-guided deterministic folding.",
      path: "/models/fractal",
      icon: Atom,
      features: [
        "Protein structure prediction",
        "Distance & contact maps",
        "Backbone torsion angles",
        "Confidence estimation"
      ],
      models: ["FRACTAL-3B"],
      color: "primary",
      specs: {
        parameters: "3B",
        baseModel: "ESM-2 3B",
        maxLength: "1024 residues",
        license: "FOCL"
      }
    }
  ];

  return (
    <div className="relative">
      <Background3D />
      
      {/* Hero Section */}
      <section className="relative pt-24 pb-16">
        <div className="max-w-[1440px] mx-auto px-6">
          <div className="text-center mb-16">
            <h1 className="text-5xl md:text-6xl font-semibold mb-8 leading-tight">
              Cutting-edge <span className="text-primary-500">AI Models</span>
            </h1>
            <p className={`text-xl leading-relaxed max-w-3xl mx-auto ${
              theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
            }`}>
              Our models push the boundaries of scientific AI, from protein structure prediction 
              to molecular design, built with safety and accessibility at their core.
            </p>
          </div>
          
          {/* Stats */}
          <div className="grid md:grid-cols-4 gap-8 mb-20">
            {[
              { number: "3B", label: "Parameters", icon: Code },
              { number: "1024", label: "Max Residues", icon: Atom },
              { number: "<5s", label: "Inference Time", icon: Zap },
              { number: "100%", label: "Open Source", icon: ArrowUpRight }
            ].map((stat, index) => (
              <div
                key={index}
                className={`text-center p-6 rounded-2xl ${
                  theme === 'dark'
                    ? 'bg-dark-200/50 border border-dark-300'
                    : 'bg-white/50 border border-gray-200'
                } backdrop-blur-sm`}
              >
                <div className={`w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4 ${
                  theme === 'dark' ? 'bg-primary-500/20' : 'bg-primary-100'
                }`}>
                  <stat.icon className="w-6 h-6 text-primary-500" />
                </div>
                <div className="text-3xl font-semibold mb-2">{stat.number}</div>
                <div className={`text-sm ${
                  theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                }`}>
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Model Details */}
      <section className="py-20">
        <div className="max-w-[1440px] mx-auto px-6">
          <h2 className="text-3xl font-semibold mb-12 text-center">
            Our Models
          </h2>
          
          {modelFamilies.map((family, index) => (
            <Link
              key={index}
              to={family.path}
              className={`block group p-8 rounded-2xl border transition-all duration-500 hover:scale-[1.02] ${
                theme === 'dark'
                  ? 'bg-dark-200/50 border-dark-300 hover:border-primary-500'
                  : 'bg-white/50 border-gray-200 hover:border-primary-300'
              } backdrop-blur-sm mb-8`}
            >
              <div className="flex items-start justify-between mb-6">
                <div className="flex items-start space-x-4">
                  <div className={`p-3 rounded-xl ${
                    theme === 'dark' ? 'bg-primary-500/20' : 'bg-primary-100'
                  }`}>
                    <family.icon className="w-6 h-6 text-primary-500" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-semibold mb-2">
                      <span className="text-gradient">{family.name}</span>
                    </h3>
                    <div className="flex flex-wrap gap-2 mb-3">
                      {family.models.map((model, modelIndex) => (
                        <span
                          key={modelIndex}
                          className={`px-2 py-1 rounded-full text-xs font-medium ${
                            theme === 'dark'
                              ? 'bg-dark-300 text-gray-300'
                              : 'bg-gray-100 text-gray-700'
                          }`}
                        >
                          {model}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
                <ArrowUpRight className="w-5 h-5 text-primary-500 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
              </div>
              
              <p className={`mb-6 leading-relaxed ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
              }`}>
                {family.description}
              </p>
              
              {/* Specs */}
              <div className={`grid grid-cols-2 md:grid-cols-4 gap-4 p-4 rounded-xl mb-6 ${
                theme === 'dark' ? 'bg-dark-300/50' : 'bg-gray-50'
              }`}>
                <div>
                  <div className={`text-xs font-medium mb-1 ${
                    theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                  }`}>Parameters</div>
                  <div className="text-sm font-semibold">{family.specs.parameters}</div>
                </div>
                <div>
                  <div className={`text-xs font-medium mb-1 ${
                    theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                  }`}>Base Model</div>
                  <div className="text-sm font-semibold">{family.specs.baseModel}</div>
                </div>
                <div>
                  <div className={`text-xs font-medium mb-1 ${
                    theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                  }`}>Max Length</div>
                  <div className="text-sm font-semibold">{family.specs.maxLength}</div>
                </div>
                <div>
                  <div className={`text-xs font-medium mb-1 ${
                    theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                  }`}>License</div>
                  <div className="text-sm font-semibold">{family.specs.license}</div>
                </div>
              </div>
              
              <div className="space-y-2">
                <h4 className="text-sm font-semibold">Key Capabilities</h4>
                <div className="grid grid-cols-2 gap-2">
                  {family.features.map((feature, featureIndex) => (
                    <div key={featureIndex} className="flex items-center space-x-2">
                      <div className={`w-1.5 h-1.5 rounded-full ${
                        theme === 'dark' ? 'bg-primary-400' : 'bg-primary-500'
                      }`} />
                      <span className={`text-sm ${
                        theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                      }`}>
                        {feature}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Technical Overview */}
      <section className={`py-20 ${
        theme === 'dark' ? 'bg-dark-200/30' : 'bg-gray-50/50'
      }`}>
        <div className="max-w-[1440px] mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-semibold mb-4">
              Built for Scientific Discovery
            </h2>
            <p className={`text-lg max-w-2xl mx-auto ${
              theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
            }`}>
              FRACTAL combines state-of-the-art protein language models with physics-guided 
              optimization to accelerate structural biology research and drug discovery.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                title: "Open Source",
                description: "Released under FOCL license for research and educational purposes, enabling widespread scientific collaboration.",
                icon: Code
              },
              {
                title: "Efficient Architecture",
                description: "Two-stage pipeline decouples neural prediction from structural assembly, enabling rapid experimentation.",
                icon: Zap
              },
              {
                title: "Research-Driven",
                description: "Trained on high-resolution crystal structures with interpretable geometric constraints for transparent predictions.",
                icon: Atom
              }
            ].map((item, index) => (
              <div
                key={index}
                className={`p-6 rounded-xl ${
                  theme === 'dark' ? 'bg-dark-200/50' : 'bg-white/70'
                } backdrop-blur-sm`}
              >
                <div className={`w-12 h-12 rounded-full flex items-center justify-center mb-4 ${
                  theme === 'dark' ? 'bg-primary-500/20' : 'bg-primary-100'
                }`}>
                  <item.icon className="w-6 h-6 text-primary-500" />
                </div>
                <h3 className="text-lg font-semibold mb-3">{item.title}</h3>
                <p className={`text-sm leading-relaxed ${
                  theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                }`}>
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}